/**
 * Tava Order Tracking — client lookup against a configured tracking API.
 * Validates input, rate-limits submits, and renders real API results only.
 * Does not fabricate shipment data when the endpoint is missing or fails.
 */
(function () {
  'use strict';

  var TRACKING_RE = /^[A-Za-z0-9][A-Za-z0-9\-_\s]{5,48}$/;
  var SUBMIT_COOLDOWN_MS = 5000;
  var MAX_TRACKING_LEN = 48;

  /**
   * Boots tracking UI for each section root on the page.
   * @returns {void}
   */
  function initAll() {
    document.querySelectorAll('[data-tava-track-order]').forEach(initTracker);
  }

  /**
   * Wires one track-order section: form, loading, and results rendering.
   * @param {HTMLElement} root - Section root with data attributes
   * @returns {void}
   */
  function initTracker(root) {
    var configured = root.getAttribute('data-api-configured') === 'true';
    var form = root.querySelector('[data-tava-track-order-form]');
    if (!configured || !form) return;

    var apiUrl = (root.getAttribute('data-tracking-api-url') || '').trim();
    var notFoundMessage =
      root.getAttribute('data-not-found-message') ||
      'We could not find a shipment for that tracking number.';

    var trackingInput = form.querySelector('input[name="tracking_number"]');
    var errorEl = form.querySelector('[id$="-tracking-error"]');
    var errorText = errorEl && errorEl.querySelector('[data-error-text]');
    var statusEl = form.querySelector('[id$="-status"]');
    var submitBtn = form.querySelector('[data-submit-btn]');
    var spinner = form.querySelector('[data-btn-spinner]');
    var resultsEl = root.querySelector('[id$="-results"]');

    var lastSubmitAt = 0;
    var busy = false;

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      if (busy) return;

      var now = Date.now();
      if (now - lastSubmitAt < SUBMIT_COOLDOWN_MS) {
        setStatus(statusEl, 'Please wait a moment before trying again.', 'info');
        return;
      }

      clearFieldError(trackingInput, errorEl, errorText);
      hideStatus(statusEl);
      hideResults(resultsEl);

      var raw = (trackingInput.value || '').trim();
      var trackingNumber = raw.replace(/\s+/g, '');

      if (!trackingNumber) {
        setFieldError(
          trackingInput,
          errorEl,
          errorText,
          'Please enter a tracking number.'
        );
        trackingInput.focus();
        return;
      }

      if (trackingNumber.length > MAX_TRACKING_LEN || !TRACKING_RE.test(raw)) {
        setFieldError(
          trackingInput,
          errorEl,
          errorText,
          'Enter a valid tracking number (letters, numbers, and hyphens only).'
        );
        trackingInput.focus();
        return;
      }

      lastSubmitAt = now;
      busy = true;
      setLoading(true, submitBtn, spinner);
      setStatus(statusEl, 'Looking up your shipment…', 'info');

      lookupTracking(apiUrl, trackingNumber)
        .then(function (payload) {
          if (!payload || payload.found === false) {
            setStatus(statusEl, notFoundMessage, 'error');
            return;
          }
          if (payload.error) {
            setStatus(
              statusEl,
              payload.message ||
                'We could not look up that tracking number. Please try again.',
              'error'
            );
            return;
          }
          hideStatus(statusEl);
          renderResults(resultsEl, payload, trackingNumber);
          setStatus(statusEl, 'Shipment details found.', 'success');
        })
        .catch(function () {
          setStatus(
            statusEl,
            'Tracking is temporarily unavailable. Please try again shortly.',
            'error'
          );
        })
        .finally(function () {
          setLoading(false, submitBtn, spinner);
          busy = false;
        });
    });
  }

  /**
   * Calls the merchant tracking endpoint with a sanitized tracking number.
   * @param {string} apiUrl - Configured HTTPS endpoint
   * @param {string} trackingNumber - Sanitized tracking number
   * @returns {Promise<object>} Parsed JSON response
   */
  function lookupTracking(apiUrl, trackingNumber) {
    var url;
    try {
      url = new URL(apiUrl, window.location.origin);
    } catch (err) {
      return Promise.reject(err);
    }

    // Prefer POST; fall back documented for GET-style proxies
    return fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ tracking_number: trackingNumber }),
      credentials: 'same-origin',
    }).then(function (res) {
      if (res.status === 404) {
        return { found: false };
      }
      if (res.status === 429) {
        return {
          error: true,
          message: 'Too many requests. Please wait a moment and try again.',
        };
      }
      return res.json().then(
        function (data) {
          if (!res.ok) {
            return {
              error: true,
              message: (data && data.message) || undefined,
              found: data && data.found,
            };
          }
          return data || {};
        },
        function () {
          return {
            error: true,
            message: 'Unexpected response from the tracking service.',
          };
        }
      );
    });
  }

  /**
   * Renders shipment details into the results container from API data.
   * @param {HTMLElement|null} resultsEl
   * @param {object} data - Tracking API payload
   * @param {string} fallbackNumber - Number the customer entered
   * @returns {void}
   */
  function renderResults(resultsEl, data, fallbackNumber) {
    if (!resultsEl) return;

    var status = escapeHtml(data.status || data.shipment_status || 'In transit');
    var number = escapeHtml(data.tracking_number || fallbackNumber);
    var carrier = escapeHtml(data.carrier || data.carrier_name || '');
    var eta = escapeHtml(data.estimated_delivery || data.eta || '');
    var latest = escapeHtml(
      data.latest_update ||
        data.last_update ||
        (data.events && data.events[0] && data.events[0].description) ||
        ''
    );
    var carrierUrl = sanitizeUrl(data.carrier_url || data.tracking_url || '');

    var html = '';
    html += '<p class="main-track-order__result-status">' + status + '</p>';
    html += '<ul class="main-track-order__meta" role="list">';
    html +=
      '<li class="main-track-order__meta-item"><span class="main-track-order__meta-label">Tracking number</span><span class="main-track-order__meta-value">' +
      number +
      '</span></li>';
    if (carrier) {
      html +=
        '<li class="main-track-order__meta-item"><span class="main-track-order__meta-label">Carrier</span><span class="main-track-order__meta-value">' +
        carrier +
        '</span></li>';
    }
    if (eta) {
      html +=
        '<li class="main-track-order__meta-item"><span class="main-track-order__meta-label">Estimated delivery</span><span class="main-track-order__meta-value">' +
        eta +
        '</span></li>';
    }
    html += '</ul>';

    if (latest) {
      html +=
        '<div class="main-track-order__latest"><span class="main-track-order__latest-label">Latest update</span><p class="main-track-order__latest-text">' +
        latest +
        '</p></div>';
    }

    var events = Array.isArray(data.events)
      ? data.events
      : Array.isArray(data.history)
        ? data.history
        : [];
    if (events.length) {
      html += '<h3 class="main-track-order__timeline-title">Shipment timeline</h3>';
      html += '<ol class="main-track-order__timeline">';
      events.forEach(function (evt) {
        var when = escapeHtml(evt.date || evt.timestamp || evt.time || '');
        var desc = escapeHtml(evt.description || evt.status || evt.message || '');
        if (!desc) return;
        html += '<li class="main-track-order__timeline-item">';
        if (when) {
          html += '<span class="main-track-order__timeline-date">' + when + '</span>';
        }
        html += '<p class="main-track-order__timeline-text">' + desc + '</p>';
        html += '</li>';
      });
      html += '</ol>';
    }

    if (carrierUrl) {
      html +=
        '<a class="main-track-order__carrier-link link" href="' +
        carrierUrl +
        '" target="_blank" rel="noopener noreferrer">View on carrier website</a>';
    }

    resultsEl.innerHTML = html;
    resultsEl.hidden = false;
  }

  /**
   * Escapes text for safe HTML insertion.
   * @param {string} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Allows only http(s) URLs for carrier tracking links.
   * @param {string} value
   * @returns {string} Safe URL or empty string
   */
  function sanitizeUrl(value) {
    if (!value) return '';
    try {
      var parsed = new URL(value, window.location.origin);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.toString();
      }
    } catch (err) {
      return '';
    }
    return '';
  }

  /**
   * Toggles submit loading UI.
   * @param {boolean} loading
   * @param {HTMLButtonElement|null} btn
   * @param {HTMLElement|null} spinner
   * @returns {void}
   */
  function setLoading(loading, btn, spinner) {
    if (btn) btn.disabled = loading;
    if (spinner) spinner.hidden = !loading;
  }

  /**
   * Sets a live status message.
   * @param {HTMLElement|null} el
   * @param {string} message
   * @param {'success'|'error'|'info'} type
   * @returns {void}
   */
  function setStatus(el, message, type) {
    if (!el) return;
    el.hidden = false;
    el.className =
      'main-track-order__status main-track-order__status--' + (type || 'info');
    el.textContent = message;
  }

  /**
   * Hides the status region.
   * @param {HTMLElement|null} el
   * @returns {void}
   */
  function hideStatus(el) {
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  /**
   * Clears and hides the results panel.
   * @param {HTMLElement|null} el
   * @returns {void}
   */
  function hideResults(el) {
    if (!el) return;
    el.hidden = true;
    el.innerHTML = '';
  }

  /**
   * Shows a field-level validation error.
   * @param {HTMLInputElement|null} input
   * @param {HTMLElement|null} errorEl
   * @param {HTMLElement|null} errorText
   * @param {string} message
   * @returns {void}
   */
  function setFieldError(input, errorEl, errorText, message) {
    if (input) input.setAttribute('aria-invalid', 'true');
    if (errorEl) errorEl.hidden = false;
    if (errorText) errorText.textContent = message;
  }

  /**
   * Clears field-level validation error state.
   * @param {HTMLInputElement|null} input
   * @param {HTMLElement|null} errorEl
   * @param {HTMLElement|null} errorText
   * @returns {void}
   */
  function clearFieldError(input, errorEl, errorText) {
    if (input) input.removeAttribute('aria-invalid');
    if (errorEl) errorEl.hidden = true;
    if (errorText) errorText.textContent = '';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
